/**
 * [2026-09-10] 웹 백엔드 인증 API 클라이언트.
 *   - 개발(vite 5180): 백엔드는 http://localhost:8081 (CORS 허용). SSH 터널로 GCP 웹DB 연결된 로컬 bootRun.
 *   - 배포: same-origin('') — Nginx가 /api/web/** → 웹 Tomcat(8081)로 프록시(P6).
 *   토큰은 localStorage에 보관하고 Authorization: Bearer 로 전송(쿠키 미사용).
 */

// import.meta.env.DEV = vite dev 모드. 배포 빌드에선 same-origin.
const API_BASE = import.meta.env.DEV ? 'http://localhost:8081' : '';
const PREFIX = '/api/web/auth';
const TOKEN_KEY = 'evs_web_token';

export function getToken(): string | null {
	try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null) {
	try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}

export interface ApiResult { success: boolean; message?: string; [k: string]: unknown; }

/** 공통 호출. 인증 필요 시 Bearer 첨부. 401(단일세션 만료 등)은 onUnauthorized로 통지. */
export async function api(path: string, opts: { method?: string; body?: unknown; auth?: boolean } = {}): Promise<ApiResult> {
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	if (opts.auth) {
		const t = getToken();
		if (t) headers.Authorization = `Bearer ${t}`;
	}
	let res: Response;
	try {
		res = await fetch(API_BASE + PREFIX + path, {
			method: opts.method || (opts.body ? 'POST' : 'GET'),
			headers,
			body: opts.body ? JSON.stringify(opts.body) : undefined,
		});
	} catch {
		return { success: false, message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.' };
	}
	if (res.status === 401 && opts.auth) {
		onUnauthorized?.();
	}
	try { return await res.json() as ApiResult; }
	catch { return { success: false, message: `요청 실패 (${res.status})` }; }
}

// 단일세션 만료·토큰 무효(401) 발생 시 AuthStore가 로그아웃 처리하도록 연결(순환 import 회피).
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn; }

// ── 엔드포인트 래퍼 ──
export const authApi = {
	login: (loginId: string, password: string) => api('/login', { body: { loginId, password } }),
	logout: () => api('/logout', { method: 'POST', auth: true }),
	me: () => api('/me', { auth: true }),
	changePassword: (currentPassword: string, newPassword: string) =>
		api('/me/password', { body: { currentPassword, newPassword }, auth: true }),
	// [2026-09-11 #5] 소셜 전용 계정(비번 없음)의 최초 비번 설정 — 현재 비번 대조 없음. 성공 후 email+비번 로그인 가능.
	setPassword: (newPassword: string) => api('/me/password/set', { body: { newPassword }, auth: true }),
	withdraw: (password?: string) => api('/me/withdraw', { body: { password: password || '' }, auth: true }),
	getMarketing: () => api('/me/marketing', { auth: true }),
	setMarketing: (smsOpt: boolean, emailOpt: boolean) => api('/me/marketing', { body: { smsOpt, emailOpt }, auth: true }),
	// 회원가입
	checkUsername: (username: string) => api('/check-username?username=' + encodeURIComponent(username)),
	phoneSend: (phone: string, purpose = 'SIGNUP') => api('/phone/send', { body: { phone, purpose } }),
	phoneVerify: (phone: string, code: string, purpose = 'SIGNUP') => api('/phone/verify', { body: { phone, code, purpose } }),
	signup: (data: Record<string, unknown>) => api('/signup', { body: data }),
	// 찾기
	findId: (phone: string) => api('/find-id', { body: { phone } }),
	resetPassword: (loginId: string, phone: string, newPassword: string) =>
		api('/reset-password', { body: { loginId, phone, newPassword } }),
	// 문의(로그인) — [2026-09-11] 멀티파트(텍스트 + 선택 첨부 1건). FormData로 전송.
	inquiryCreate: async (fd: FormData): Promise<ApiResult> => {
		const t = getToken();
		let res: Response;
		try {
			res = await fetch(API_BASE + PREFIX + '/inquiries', { method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd });
		} catch { return { success: false, message: '서버에 연결할 수 없습니다.' }; }
		if (res.status === 401) onUnauthorized?.();
		try { return await res.json() as ApiResult; } catch { return { success: false, message: `요청 실패 (${res.status})` }; }
	},
	// [2026-09-11] 첨부 다운로드 — Bearer 인증이라 fetch로 blob 받아 저장. @return null=성공, 아니면 오류.
	downloadInquiryAttachment: async (id: number, filename: string): Promise<string | null> => {
		const t = getToken();
		let res: Response;
		try { res = await fetch(API_BASE + PREFIX + `/inquiries/${id}/attachment`, { headers: t ? { Authorization: `Bearer ${t}` } : {} }); }
		catch { return '서버에 연결할 수 없습니다.'; }
		if (!res.ok) return `다운로드 실패 (${res.status})`;
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a'); a.href = url; a.download = filename || 'attachment';
		document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
		return null;
	},
	inquiryMine: (page: number, size = 10) => api(`/inquiries/mine?page=${page}&size=${size}`, { auth: true }),
	inquiryDetail: (id: number) => api(`/inquiries/${id}`, { auth: true }),
	inquiryEdit: (id: number, title: string, content: string) => api(`/inquiries/${id}`, { method: 'PUT', body: { title, content }, auth: true }),
	inquiryDelete: (id: number) => api(`/inquiries/${id}`, { method: 'DELETE', auth: true }),
	// 소셜(Google·Naver)
	socialAuthorize: (provider: string) => api(`/social/${provider}/authorize`),
	socialLogin: (provider: string, code: string, redirectUri: string, state?: string) =>
		api(`/social/${provider}`, { body: { code, redirectUri, state } }),
	// [2026-09-11 #4] 소셜 신규가입 완료단계 확정 — 휴대폰 인증·필수약관·개인정보 유효기간. PENDING 토큰으로 호출(auth).
	completeSocial: (data: Record<string, unknown>) => api('/me/complete-social', { body: data, auth: true }),
	// [2026-09-12] A/S(사후관리) — 회원 전용(/api/web/auth/as). 신청=멀티파트(제품·증상+선택첨부), 이후 상태추적은 서버 타임라인.
	asCreate: async (fd: FormData): Promise<ApiResult> => {
		const t = getToken();
		let res: Response;
		try { res = await fetch(API_BASE + PREFIX + '/as', { method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd }); }
		catch { return { success: false, message: '서버에 연결할 수 없습니다.' }; }
		if (res.status === 401) onUnauthorized?.();
		try { return await res.json() as ApiResult; } catch { return { success: false, message: `요청 실패 (${res.status})` }; }
	},
	asMine: (page: number, size = 10, from = '', to = '') => api(`/as/mine?page=${page}&size=${size}&from=${from}&to=${to}`, { auth: true }),
	asDetail: (id: number) => api(`/as/${id}`, { auth: true }),
	asMessage: (id: number, note: string) => api(`/as/${id}/message`, { body: { note }, auth: true }),
	asCancel: (id: number) => api(`/as/${id}/cancel`, { method: 'POST', auth: true }),
	// [2026-09-12] 고객 수령·완료 확인(관리자 완료 후)
	asConfirm: (id: number) => api(`/as/${id}/confirm`, { method: 'POST', auth: true }),
};

// 관리자(ADMIN 전용) — /api/web/admin. 별도 PREFIX라 api() 대신 adminApi로.
const ADMIN_BASE = (import.meta.env.DEV ? 'http://localhost:8081' : '') + '/api/web/admin';
async function adminReq(path: string, method = 'GET', body?: unknown): Promise<ApiResult> {
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	const t = getToken();
	if (t) headers.Authorization = `Bearer ${t}`;
	let res: Response;
	try {
		res = await fetch(ADMIN_BASE + path, { method, headers, body: body ? JSON.stringify(body) : undefined });
	} catch { return { success: false, message: '서버에 연결할 수 없습니다.' }; }
	try { return await res.json() as ApiResult; } catch { return { success: false, message: `요청 실패 (${res.status})` }; }
}
export const adminApi = {
	listUsers: (keyword: string, page: number, size = 20, status = '', marketing = '', provider = '', joined = '') =>
		adminReq(`/users?keyword=${encodeURIComponent(keyword)}&status=${encodeURIComponent(status)}&marketing=${encodeURIComponent(marketing)}&provider=${encodeURIComponent(provider)}&joined=${encodeURIComponent(joined)}&page=${page}&size=${size}`),
	// [2026-09-11] 방문 로그(최근순·페이징)
	visits: (page: number, size = 30) => adminReq(`/visits?page=${page}&size=${size}`),
	setStatus: (id: number, status: 'ACTIVE' | 'SUSPENDED') => adminReq(`/users/${id}/status`, 'POST', { status }),
	deleteUser: (id: number) => adminReq(`/users/${id}`, 'DELETE'),
	// [2026-09-11] ERP 대시보드 집계. range=그래프 기간: '30d'(일별) | '6m'|'12m'|'24m'(월별)
	stats: (range = '12m') => adminReq(`/stats?range=${range}`),
	// [2026-09-12] 웹 SMS(SOLAPI) 발송 현황 — phone_verify 기반. stats+추이 / 발송내역(게시판)
	smsStats: (range = '30d') => adminReq(`/sms/stats?range=${range}`),
	smsList: (page: number, size = 20, purpose = '', verified = '') => adminReq(`/sms/list?page=${page}&size=${size}&purpose=${encodeURIComponent(purpose)}&verified=${encodeURIComponent(verified)}`),
	// [2026-09-11] 특정 회원이 쓴 문의(1:1·AS) 이력 — 회원 상세 패널
	userInquiries: (id: number) => adminReq(`/users/${id}/inquiries`),
	// [2026-09-11 CRM] 회원 상세 부가 — 방문 이력(타임라인)·메모(이력형)·태그
	userVisits: (id: number) => adminReq(`/users/${id}/visits`),
	listNotes: (id: number) => adminReq(`/users/${id}/notes`),
	addNote: (id: number, content: string) => adminReq(`/users/${id}/notes`, 'POST', { content }),
	deleteNote: (noteId: number) => adminReq(`/notes/${noteId}`, 'DELETE'),
	setTags: (id: number, tags: string) => adminReq(`/users/${id}/tags`, 'POST', { tags }),
	// [2026-09-11] 회원명부 CSV 다운로드 — Bearer 인증이라 <a href>로는 불가 → fetch로 blob 받아 다운로드 트리거.
	exportUsers: async (): Promise<string | null> => {
		const t = getToken();
		let res: Response;
		try {
			res = await fetch(ADMIN_BASE + '/users/export', { headers: t ? { Authorization: `Bearer ${t}` } : {} });
		} catch { return '서버에 연결할 수 없습니다.'; }
		if (!res.ok) return `다운로드 실패 (${res.status})`;
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url; a.download = 'evs_members.csv';
		document.body.appendChild(a); a.click(); a.remove();
		URL.revokeObjectURL(url);
		return null;
	},
	// 문의 관리
	inquiries: (keyword: string, category: string, status: string, page: number, size = 20) =>
		adminReq(`/inquiries?keyword=${encodeURIComponent(keyword)}&category=${category}&status=${status}&page=${page}&size=${size}`),
	// 상세는 authApi.inquiryDetail(id) 재사용(백엔드가 관리자 열람 허용). 답변만 /admin.
	answerInquiry: (id: number, answer: string) => adminReq(`/inquiries/${id}/answer`, 'POST', { answer }),
	// [2026-09-12] A/S 관리(/api/web/admin/as) — 목록(도메인·상태·기간 필터)·상세(타임라인)·상태전이·메시지.
	asList: (keyword: string, domain: string, status: string, from: string, to: string, page: number, size = 20) =>
		adminReq(`/as?keyword=${encodeURIComponent(keyword)}&domain=${domain}&status=${status}&from=${from}&to=${to}&page=${page}&size=${size}`),
	asDetail: (id: number) => adminReq(`/as/${id}`),
	asSetStatus: (id: number, status: string, note: string) => adminReq(`/as/${id}/status`, 'POST', { status, note }),
	// [2026-09-12] ERP 메타: 방문예정일(YYYY-MM-DD)·보증(FREE/PAID)·담당기사(+전화, 관리자 내부용)
	asSetMeta: (id: number, visitDate: string, warranty: string, technician: string, technicianPhone: string) =>
		adminReq(`/as/${id}/meta`, 'POST', { visitDate, warranty, technician, technicianPhone }),
	// [2026-09-13] 케이스별 단계 구성(순서+활성) 저장 — plan=[{key,active}]. 확정(잠금) 후엔 서버가 거부.
	asSetPlan: (id: number, plan: { key: string; active: boolean }[]) => adminReq(`/as/${id}/plan`, 'POST', { plan }),
	// [2026-09-13] 구성 확정·진행 시작(잠금) / 재구성(사유 필수, 잠금 해제). BPM 원리: 실행 인스턴스 정의 불변.
	asCommit: (id: number) => adminReq(`/as/${id}/commit`, 'POST', {}),
	asReconfigure: (id: number, reason: string) => adminReq(`/as/${id}/reconfigure`, 'POST', { reason }),
	asMessage: (id: number, note: string) => adminReq(`/as/${id}/message`, 'POST', { note }),
};
